import React, { useEffect, useState } from 'react';
import { G, styles, api, fmtUsd } from '../theme.js';
import { Btn, Badge, Input, Select, Field, Modal, Spinner } from '../ui.jsx';

export function Memberships({ toast }) {
  const [tab, setTab] = useState('members');
  const [data, setData] = useState({ summary:{}, memberships:[] });
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contacts, setContacts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [editingPlan, setEditingPlan] = useState(null);
  const [enrolling, setEnrolling] = useState(null);

  const load = () => Promise.all([
    api('/api/memberships'),
    api('/api/contacts').catch(()=>[]),
    api('/api/memberships/plans').catch(()=>[]),
  ]).then(([m,c,p]) => { setData(m); setContacts(c); setPlans(p); })
    .catch(e=>toast(e.message,'error'))
    .finally(()=>setLoading(false));
  useEffect(() => { load(); }, []);

  if (loading) return <div style={{ display:'flex', justifyContent:'center', padding:80 }}><Spinner sz={32}/></div>;

  return <div style={{ padding:24 }}>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
      <h1 style={{ margin:0, fontSize:24, fontWeight:800 }}>Memberships</h1>
      <div style={{ display:'flex', gap:8 }}>
        {tab === 'members' && <Btn v="primary" onClick={()=>setEnrolling({ planId: plans[0]?.id || '', billingCycle:'monthly' })} disabled={!plans.length}>+ Enroll Member</Btn>}
        {tab === 'plans' && <Btn v="primary" onClick={()=>setEditingPlan({ name:'', monthlyPrice:25, annualPrice:299, visitsPerYear:2, memberDiscountPct:10, benefits:[] })}>+ New Plan</Btn>}
      </div>
    </div>

    <div style={{ display:'flex', gap:6, marginBottom:18, background:G.input, padding:4, borderRadius:10, width:'fit-content' }}>
      <Btn sz="sm" v={tab==='members'?'primary':'ghost'} onClick={()=>setTab('members')}>👥 Members ({data.memberships.length})</Btn>
      <Btn sz="sm" v={tab==='plans'?'primary':'ghost'} onClick={()=>setTab('plans')}>📋 Plans ({plans.length})</Btn>
    </div>

    {tab === 'members' && <>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))', gap:14, marginBottom:20 }}>
        <Stat label="Active Members" value={data.summary.active} sub={`${data.summary.total} total`} color={G.green}/>
        <Stat label="Monthly Recurring" value={fmtUsd(data.summary.monthlyMrr)} sub="MRR from memberships" color={G.purple}/>
        <Stat label="Annual Value" value={fmtUsd(data.summary.annualValue)} sub="Trailing 12mo run-rate" color={G.cyan}/>
      </div>
      <div style={{ ...styles.card, padding:0, overflow:'hidden' }}>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead><tr>{['Customer','Plan','Status','Cycle','Monthly','Annual','Visits','Next Visit',''].map(h => <th key={h} style={styles.th}>{h}</th>)}</tr></thead>
          <tbody>{data.memberships.map(m => (
            <tr key={m.id} onClick={()=>setEditing(m)} style={{ cursor:'pointer' }}>
              <td style={{ ...styles.td, fontWeight:600 }}>{m.contactName||'—'}</td>
              <td style={styles.td}>{m.planName}</td>
              <td style={styles.td}><Badge c={m.status==='active'?G.green:G.textMuted} bg={(m.status==='active'?G.green:G.textMuted)+'22'}>{m.status}</Badge></td>
              <td style={styles.td}>{m.billingCycle}</td>
              <td style={styles.td}>{fmtUsd(m.monthlyPrice)}</td>
              <td style={styles.td}>{fmtUsd(m.annualPrice)}</td>
              <td style={styles.td}>{m.visitsUsed}/{m.visitsPerYear}</td>
              <td style={{ ...styles.td, fontSize:12, color:G.textMuted }}>{m.nextVisitDue||'—'}</td>
              <td style={styles.td}/>
            </tr>
          ))}</tbody>
        </table>
        {data.memberships.length === 0 && <div style={{ padding:48, textAlign:'center', color:G.textMuted }}>
          {plans.length === 0 ? 'Create a plan first, then enroll members into it.' : 'No memberships yet. Click "+ Enroll Member" to add one.'}
        </div>}
      </div>
    </>}

    {tab === 'plans' && <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:14 }}>
      {plans.map(p => (
        <div key={p.id} style={{ ...styles.card, padding:18, borderColor: p.active?G.purple+'66':G.cardBorder }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:6 }}>
            <strong style={{ fontSize:16 }}>{p.name}</strong>
            {!p.active && <Badge c={G.textMuted} bg={G.input}>archived</Badge>}
          </div>
          {p.description && <div style={{ fontSize:12, color:G.textMuted, marginBottom:8 }}>{p.description}</div>}
          <div style={{ fontSize:22, fontWeight:800, color:G.purple, marginBottom:2 }}>{fmtUsd(p.monthlyPrice)}<span style={{ fontSize:12, color:G.textMuted, fontWeight:500 }}>/mo</span></div>
          <div style={{ fontSize:12, color:G.textMuted, marginBottom:10 }}>or {fmtUsd(p.annualPrice)}/yr</div>
          <ul style={{ margin:'0 0 12px', paddingLeft:18, color:G.textMuted, fontSize:13, lineHeight:1.6 }}>
            <li>{p.visitsPerYear} maintenance visit{p.visitsPerYear===1?'':'s'}/yr</li>
            {p.memberDiscountPct > 0 && <li>{p.memberDiscountPct}% off services</li>}
            {(p.benefits||[]).map((b,i) => <li key={i}>{b}</li>)}
          </ul>
          <div style={{ display:'flex', gap:6 }}>
            <Btn sz="sm" onClick={()=>setEditingPlan(p)}>Edit</Btn>
            <Btn sz="sm" v="primary" onClick={()=>setEnrolling({ planId: p.id, billingCycle:'monthly' })}>Enroll</Btn>
          </div>
        </div>
      ))}
      {plans.length === 0 && <div style={{ ...styles.card, padding:32, textAlign:'center', color:G.textMuted, gridColumn:'1/-1' }}>
        No plans yet. Build your first one (e.g. "Gold Care — $19/mo, 2 tune-ups/yr, 10% off parts") to start enrolling members.
      </div>}
    </div>}

    <MembershipModal m={editing} contacts={contacts} onClose={()=>setEditing(null)} onSaved={()=>{ load(); setEditing(null); toast('Saved'); }}/>
    {editingPlan && <PlanModal plan={editingPlan} onClose={()=>setEditingPlan(null)} onSaved={()=>{ load(); setEditingPlan(null); toast('Plan saved'); }} toast={toast}/>}
    {enrolling && <EnrollModal preset={enrolling} plans={plans} contacts={contacts} onClose={()=>setEnrolling(null)} onDone={()=>{ load(); setEnrolling(null); toast('✓ Member enrolled'); }} toast={toast}/>}
  </div>;
}

function PlanModal({ plan, onClose, onSaved, toast }) {
  const [f, setF] = useState(plan);
  const isNew = !f.id;
  const benefitText = (f.benefits||[]).join('\n');
  const save = async () => {
    if (!f.name?.trim()) return toast('Plan name required', 'error');
    try {
      const body = { ...f };
      if (isNew) await api('/api/memberships/plans', { method:'POST', body });
      else await api(`/api/memberships/plans/${f.id}`, { method:'PATCH', body });
      onSaved();
    } catch (e) { toast(e.message, 'error'); }
  };
  return <Modal open onClose={onClose} width={560}>
    <h2 style={{ margin:'0 0 14px 0', fontSize:18 }}>{isNew ? 'New Plan' : f.name}</h2>
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
      <Field label="Name *" style={{ gridColumn:'1/-1' }}><Input value={f.name||''} onChange={e=>setF({...f, name:e.target.value})} placeholder="Gold Care, Comfort Club, etc."/></Field>
      <Field label="Description" style={{ gridColumn:'1/-1' }}><Input value={f.description||''} onChange={e=>setF({...f, description:e.target.value})}/></Field>
      <Field label="Monthly Price"><Input type="number" step="0.01" value={f.monthlyPrice||0} onChange={e=>setF({...f, monthlyPrice:Number(e.target.value)})}/></Field>
      <Field label="Annual Price"><Input type="number" step="0.01" value={f.annualPrice||0} onChange={e=>setF({...f, annualPrice:Number(e.target.value)})}/></Field>
      <Field label="Visits / Year"><Input type="number" value={f.visitsPerYear||2} onChange={e=>setF({...f, visitsPerYear:Number(e.target.value)})}/></Field>
      <Field label="Member Discount %"><Input type="number" step="0.5" value={f.memberDiscountPct||0} onChange={e=>setF({...f, memberDiscountPct:Number(e.target.value)})}/></Field>
      <Field label="Benefits (one per line)" style={{ gridColumn:'1/-1' }}>
        <textarea value={benefitText} onChange={e=>setF({...f, benefits: e.target.value.split('\n').map(s=>s.trim()).filter(Boolean)})}
          rows={4} style={{ width:'100%', padding:10, background:G.input, color:G.text, border:`1px solid ${G.cardBorder}`, borderRadius:8, fontSize:13, fontFamily:'inherit', resize:'vertical' }}
          placeholder="Free service call ($75 value)&#10;Priority scheduling&#10;No after-hours fees"/>
      </Field>
    </div>
    <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:14 }}>
      <Btn onClick={onClose}>Cancel</Btn>
      <Btn v="primary" onClick={save}>{isNew ? 'Create Plan' : 'Save'}</Btn>
    </div>
  </Modal>;
}

function EnrollModal({ preset, plans, contacts, onClose, onDone, toast }) {
  const [f, setF] = useState(preset);
  const [busy, setBusy] = useState(false);
  const enroll = async () => {
    if (!f.contactId || !f.planId) return toast('Pick customer + plan', 'error');
    setBusy(true);
    try {
      await api('/api/memberships/enroll', { method:'POST', body: f });
      onDone();
    } catch (e) { toast(e.message, 'error'); }
    finally { setBusy(false); }
  };
  return <Modal open onClose={onClose} width={440}>
    <h2 style={{ margin:'0 0 14px 0', fontSize:18 }}>⭐ Enroll Member</h2>
    <Field label="Customer">
      <Select value={f.contactId||''} onChange={e=>setF({...f, contactId:e.target.value})}>
        <option value="">— select —</option>
        {contacts.map(c => <option key={c.id} value={c.id}>{c.firstName} {c.lastName}{c.phone?` · ${c.phone}`:''}</option>)}
      </Select>
    </Field>
    <Field label="Plan">
      <Select value={f.planId||''} onChange={e=>setF({...f, planId:e.target.value})}>
        <option value="">— select —</option>
        {plans.filter(p => p.active).map(p => <option key={p.id} value={p.id}>{p.name} · {fmtUsd(p.monthlyPrice)}/mo</option>)}
      </Select>
    </Field>
    <Field label="Billing">
      <Select value={f.billingCycle||'monthly'} onChange={e=>setF({...f, billingCycle:e.target.value})}>
        <option value="monthly">Monthly</option>
        <option value="annual">Annual</option>
      </Select>
    </Field>
    <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:14 }}>
      <Btn onClick={onClose} disabled={busy}>Cancel</Btn>
      <Btn v="primary" onClick={enroll} disabled={busy}>{busy ? <Spinner sz={14}/> : '⭐ Enroll'}</Btn>
    </div>
  </Modal>;
}

function Stat({ label, value, sub, color }) {
  return <div style={{ ...styles.card, borderColor: color+'66' }}>
    <div style={{ fontSize:11, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.05em' }}>{label}</div>
    <div style={{ fontSize:24, fontWeight:800, color, marginTop:6 }}>{value}</div>
    <div style={{ fontSize:12, color:G.textMuted, marginTop:4 }}>{sub}</div>
  </div>;
}

function MembershipModal({ m, contacts, onClose, onSaved }) {
  const [form, setForm] = useState({});
  useEffect(() => { setForm(m||{}); }, [m]);
  if (!m) return null;
  const isNew = !form.id;
  const save = async () => {
    try {
      if (isNew) await api('/api/memberships', { method:'POST', body: form });
      else await api(`/api/memberships/${form.id}`, { method:'PATCH', body: form });
      onSaved();
    } catch(e) { alert(e.message); }
  };
  return <Modal open onClose={onClose} width={520}>
    <h2 style={{ margin:'0 0 18px 0' }}>{isNew?'New Membership':form.planName}</h2>
    <Field label="Customer">
      <Select value={form.contactId||''} onChange={e=>setForm({...form,contactId:e.target.value})}>
        <option value="">— select —</option>
        {contacts.map(c => <option key={c.id} value={c.id}>{c.firstName} {c.lastName}</option>)}
      </Select>
    </Field>
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
      <Field label="Plan Name *" style={{ gridColumn:'1/-1' }}><Input value={form.planName||''} onChange={e=>setForm({...form,planName:e.target.value})} placeholder="Annual Care Plan"/></Field>
      <Field label="Monthly Price"><Input type="number" step="0.01" value={form.monthlyPrice||0} onChange={e=>setForm({...form,monthlyPrice:Number(e.target.value)})}/></Field>
      <Field label="Annual Price"><Input type="number" step="0.01" value={form.annualPrice||0} onChange={e=>setForm({...form,annualPrice:Number(e.target.value)})}/></Field>
      <Field label="Billing Cycle"><Select value={form.billingCycle||'monthly'} onChange={e=>setForm({...form,billingCycle:e.target.value})}><option>monthly</option><option>annual</option></Select></Field>
      <Field label="Visits / Year"><Input type="number" value={form.visitsPerYear||2} onChange={e=>setForm({...form,visitsPerYear:Number(e.target.value)})}/></Field>
      {!isNew && <>
        <Field label="Status"><Select value={form.status||'active'} onChange={e=>setForm({...form,status:e.target.value})}><option>active</option><option>cancelled</option><option>paused</option></Select></Field>
        <Field label="Visits Used"><Input type="number" value={form.visitsUsed||0} onChange={e=>setForm({...form,visitsUsed:Number(e.target.value)})}/></Field>
      </>}
    </div>
    <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:14 }}>
      <Btn onClick={onClose}>Cancel</Btn>
      <Btn v="primary" onClick={save}>Save</Btn>
    </div>
  </Modal>;
}
